import debounce from 'lodash/debounce';
import defer from 'lodash/defer';
import isNumber from 'lodash/isNumber';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RefObject } from 'react';

interface Options {
  defaultWidth?: number;
  minWidth?: number;
  wait?: number;
}

export const useResizeWidth = <Ref extends RefObject<HTMLElement>>(
  ref: Ref,
  { defaultWidth = 0, minWidth = 0, wait = 50 }: Options = {},
): number => {
  const [width, setWidth] = useState<number>(defaultWidth);

  const onResize = useCallback(() => {
    const offsetWidth = ref.current?.offsetWidth;

    if (isNumber(offsetWidth)) {
      setWidth(offsetWidth > minWidth ? offsetWidth : minWidth);
    }
  }, [ref, minWidth]);

  const onResizeDebounce = useMemo(
    () => debounce(onResize, wait),
    [onResize, wait],
  );

  useEffect(() => {
    const element = ref.current;
    const resizeObserver = new ResizeObserver(onResizeDebounce);

    if (!element) {
      return undefined;
    }

    defer(onResize);

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, [onResize, onResizeDebounce, ref]);

  return width;
};
